import { useState, useEffect } from "react";
import axios from "axios";
import { useParams, Link } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import {
  ArrowLeft, MapPin, Bed, Bath, Square, MessageCircle, Phone,
  CheckCircle2, Building2, Ruler, Landmark, Clock,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import Navbar from "@/components/site/Navbar";
import Footer from "@/components/site/Footer";
import { API, BACKEND_URL, SITE, waLinkWith, formatPrice } from "@/lib/site";

export default function ProjekDetail() {
  const { slug } = useParams();
  const [project, setProject] = useState(null);
  const [status, setStatus] = useState("loading");
  const [activeImg, setActiveImg] = useState(0);

  useEffect(() => {
    axios.get(`${API}/projects/${slug}`)
      .then((r) => {
        setProject(r.data);
        setStatus("ok");
        document.title = `${r.data.title} | dausREALTY`;
      })
      .catch(() => setStatus("notfound"));
  }, [slug]);

  const price = (p) => p.price_to ? `RM ${formatPrice(p.price_from)} - ${formatPrice(p.price_to)}` : p.price_from ? `Dari RM ${formatPrice(p.price_from)}` : "Harga atas permintaan";
  const imgUrl = (i) => `${BACKEND_URL}/api/projects/${project.id}/images/${i}`;
  const waMsg = project ? `Salam ${SITE.agentName}, saya berminat dengan projek "${project.title}"${project.location ? " di " + project.location : ""}. Boleh berikan butiran lanjut?` : "";

  return (
    <div className="min-h-screen bg-white" data-testid="projek-detail-page">
      <Navbar solid />
      <main className="pt-28 md:pt-32 pb-24">
        <div className="mx-auto max-w-6xl px-6">
          <Link to="/projek" className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-[#0A2540]" data-testid="projek-back-btn">
            <ArrowLeft className="h-4 w-4" /> Semua Projek
          </Link>

          {status === "loading" && <p className="mt-10 text-slate-500">Memuatkan...</p>}
          {status === "notfound" && (
            <div className="mt-10" data-testid="projek-notfound">
              <h1 className="font-heading text-2xl font-bold text-[#0A2540]">Projek tidak dijumpai</h1>
              <p className="mt-2 text-slate-600">Projek yang anda cari mungkin telah dialih atau dipadam.</p>
            </div>
          )}

          {status === "ok" && project && (
            <>
              <div className="mt-6 grid grid-cols-1 lg:grid-cols-3 gap-10">
                <div className="lg:col-span-2">
                  <div className="rounded-2xl overflow-hidden border border-slate-200 bg-slate-50 aspect-[16/10]" data-testid="projek-main-image">
                    {project.images.length > 0 ? (
                      <img src={imgUrl(activeImg)} alt={project.title} className="w-full h-full object-cover" />
                    ) : (
                      <div className="h-full w-full flex items-center justify-center text-slate-300"><Building2 className="h-16 w-16" /></div>
                    )}
                  </div>
                  {project.images.length > 1 && (
                    <div className="mt-4 grid grid-cols-5 gap-3" data-testid="projek-thumbnails">
                      {project.images.map((_, i) => (
                        <button key={i} onClick={() => setActiveImg(i)} data-testid={`thumb-${i}`}
                          className={`rounded-xl overflow-hidden border-2 aspect-square ${activeImg === i ? "border-[#1D4ED8]" : "border-transparent"}`}>
                          <img src={imgUrl(i)} alt="" className="w-full h-full object-cover" />
                        </button>
                      ))}
                    </div>
                  )}

                  <div className="mt-10">
                    <h1 className="font-heading text-3xl sm:text-4xl font-extrabold tracking-tight text-[#0A2540]">{project.title}</h1>
                    {project.location && (
                      <p className="mt-2 flex items-center gap-1 text-slate-600">
                        <MapPin className="h-4 w-4 text-[#1D4ED8]" /> {project.location}
                      </p>
                    )}
                    <p className="mt-4 font-heading text-2xl font-extrabold text-[#1D4ED8]" data-testid="projek-price">{price(project)}</p>

                    <div className="mt-6 grid grid-cols-2 sm:grid-cols-3 gap-3">
                      {[
                        { icon: Bed, label: "Bilik Tidur", value: project.bedrooms },
                        { icon: Bath, label: "Bilik Air", value: project.bathrooms },
                        { icon: Square, label: "Keluasan", value: project.built_up },
                        { icon: Building2, label: "Jenis", value: project.property_type },
                        { icon: Landmark, label: "Milikan", value: project.tenure },
                        { icon: Clock, label: "Status", value: project.status },
                      ].filter((x) => x.value).map((x) => (
                        <div key={x.label} className="rounded-xl bg-[#F8F9FA] border border-slate-200 px-4 py-3">
                          <div className="flex items-center gap-2 text-xs text-slate-500 uppercase"><x.icon className="h-3.5 w-3.5" /> {x.label}</div>
                          <p className="mt-1 font-semibold text-[#0A2540]">{x.value}</p>
                        </div>
                      ))}
                    </div>

                    {project.description && (
                      <div className="mt-10">
                        <h2 className="font-heading text-xl font-bold text-[#0A2540]">Butiran Projek</h2>
                        <div className="prose prose-slate mt-4 max-w-none prose-headings:font-heading prose-headings:text-[#0A2540] prose-a:text-[#1D4ED8]" data-testid="projek-description">
                          <ReactMarkdown>{project.description}</ReactMarkdown>
                        </div>
                      </div>
                    )}

                    {project.features && project.features.length > 0 && (
                      <div className="mt-10">
                        <h2 className="font-heading text-xl font-bold text-[#0A2540]">Kemudahan &amp; Ciri-ciri</h2>
                        <ul className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
                          {project.features.map((f) => (
                            <li key={f} className="flex items-start gap-2 text-slate-700">
                              <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-[#10B981]" /> {f}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                </div>

                <aside className="lg:col-span-1">
                  <div className="lg:sticky lg:top-28 rounded-3xl border border-slate-200 bg-white p-6 shadow-[0_8px_30px_rgb(0,0,0,0.05)]">
                    <div className="flex items-center gap-4">
                      <img src={SITE.photo} alt={SITE.agentName} className="h-16 w-16 rounded-2xl object-cover object-top border border-slate-200" />
                      <div>
                        <p className="font-heading font-bold text-[#0A2540] leading-tight">{SITE.agentName}</p>
                        <p className="text-xs text-slate-500">{SITE.agency} • {SITE.role}</p>
                      </div>
                    </div>
                    <p className="mt-5 text-sm text-slate-600 leading-relaxed">
                      Berminat dengan projek ini? Hubungi saya sekarang untuk butiran lanjut, tempahan &amp; semakan kelayakan pinjaman perumahan.
                    </p>
                    <a href={waLinkWith(waMsg)} target="_blank" rel="noopener noreferrer" data-testid="projek-whatsapp-btn" className="mt-5 block">
                      <Button className="w-full h-12 bg-[#10B981] hover:bg-[#0e9e6e] text-white rounded-full text-base transition-transform hover:scale-[1.02]">
                        <MessageCircle className="mr-2 h-5 w-5" /> WhatsApp Saya
                      </Button>
                    </a>
                    <a href={`tel:+${SITE.phoneRaw}`} data-testid="projek-call-btn" className="mt-3 block">
                      <Button variant="outline" className="w-full h-12 border-slate-300 text-[#0A2540] hover:bg-slate-50 rounded-full">
                        <Phone className="mr-2 h-5 w-5" /> {SITE.phoneDisplay}
                      </Button>
                    </a>
                    <div className="mt-6 rounded-xl bg-[#F8F9FA] p-4 flex items-start gap-3">
                      <Ruler className="h-5 w-5 shrink-0 text-[#1D4ED8]" />
                      <p className="text-xs text-slate-600 leading-relaxed">
                        Semakan kelayakan pinjaman <strong className="text-[#0A2540]">percuma</strong> dan dokumen anda dirahsiakan.
                      </p>
                    </div>
                  </div>
                </aside>
              </div>
            </>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}
